import { useState } from "react";
import type { FormEvent } from "react";
import { discoverTenant } from "@/features/auth/api/tenant-discovery-client";
import {
  clearTenantId,
  setTenantId,
  signInWithEmail,
  signInWithSso,
} from "@/features/auth/api/identity-platform-auth";
import { mapAuthErrorToMessage } from "@/features/auth/model/auth-error-messages";
import type { TenantDiscoveryResponse } from "@/features/auth/model/tenant-discovery";
import { ApiError } from "@/shared/api/api-error";

export type LoginStep = "email" | "password";
export type LoadingState = "tenant" | "password" | "sso" | null;

export interface LoginFlowState {
  step: LoginStep;
  email: string;
  password: string;
  error: string | null;
  loading: LoadingState;
  loadingSsoProviderId: string | null;
  tenantInfo: TenantDiscoveryResponse | null;
  setEmail: (email: string) => void;
  setPassword: (password: string) => void;
  handleEmailSubmit: (event: FormEvent<HTMLFormElement>) => Promise<void>;
  handlePasswordSubmit: (event: FormEvent<HTMLFormElement>) => Promise<void>;
  handleSsoSignIn: (providerId: string) => Promise<void>;
  handleBackToEmail: () => void;
}

function mapTenantDiscoveryError(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.statusCode === 404) {
      return "Email domain not recognised. Contact your administrator.";
    }

    if (error.statusCode === 400 && error.code === "invalid_email") {
      return "Please enter a valid email address.";
    }
  }

  return "We could not confirm your organisation. Please try again.";
}

export function useLoginFlow(): LoginFlowState {
  const [step, setStep] = useState<LoginStep>("email");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<LoadingState>(null);
  const [loadingSsoProviderId, setLoadingSsoProviderId] = useState<string | null>(null);
  const [tenantInfo, setTenantInfo] = useState<TenantDiscoveryResponse | null>(null);

  async function handleEmailSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setLoading("tenant");

    const normalizedEmail = email.trim().toLowerCase();

    try {
      const tenant = await discoverTenant(normalizedEmail);
      setTenantInfo(tenant);
      setTenantId(tenant.tenant.identity_platform_tenant_id);

      const hasEmailPassword = tenant.tenant.email_password_enabled;
      const hasSso = tenant.providers.length > 0;

      if (!hasEmailPassword && !hasSso) {
        setError("No supported authentication method is available for this organisation.");
        return;
      }

      setEmail(normalizedEmail);
      setStep("password");
    } catch (tenantError) {
      setError(mapTenantDiscoveryError(tenantError));
    } finally {
      setLoading(null);
    }
  }

  async function handlePasswordSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setLoading("password");

    try {
      await signInWithEmail(email, password);
    } catch (signInError) {
      setError(mapAuthErrorToMessage(signInError));
    } finally {
      setLoading(null);
    }
  }

  async function handleSsoSignIn(providerId: string) {
    setError(null);
    setLoading("sso");
    setLoadingSsoProviderId(providerId);

    try {
      await signInWithSso(providerId);
    } catch (ssoError) {
      setError(mapAuthErrorToMessage(ssoError));
      setLoading(null);
      setLoadingSsoProviderId(null);
    }
  }

  function handleBackToEmail() {
    setStep("email");
    setPassword("");
    setError(null);
    setTenantInfo(null);
    clearTenantId();
  }

  return {
    step,
    email,
    password,
    error,
    loading,
    loadingSsoProviderId,
    tenantInfo,
    setEmail,
    setPassword,
    handleEmailSubmit,
    handlePasswordSubmit,
    handleSsoSignIn,
    handleBackToEmail,
  };
}
